import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  // Cerrar sidebar al cambiar de ruta en móvil
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Cerrar sidebar al hacer clic fuera en móvil
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (window.innerWidth <= 768 && sidebarOpen) {
        const target = e.target as HTMLElement;
        if (!target.closest('.sidebar') && !target.closest('.menu-toggle')) {
          setSidebarOpen(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [sidebarOpen]);

  return (
    <div className="layout">
      <button 
        className="menu-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle menu"
      >
        ☰
      </button>
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img 
            src="/logo_enerpetrol.png" 
            alt="Enerpetrol Logo" 
            className="logo-img"
            onError={(e) => {
              console.error('Error loading logo:', e);
              // Intentar con extensión en mayúsculas como fallback
              if (e.currentTarget.src !== '/logo_enerpetrol.PNG') {
                e.currentTarget.src = '/logo_enerpetrol.PNG';
              }
            }}
          />
          <p>Sistema de Gestión</p>
        </div>

        <nav className="sidebar-nav">
          {user?.rol === 'operador' ? (
            // Menú simplificado para operadores
            <Link to="/documentos" className={isActive('/documentos') ? 'active' : ''}>
              📄 Mis Documentos
            </Link>
          ) : (
            // Menú completo para administradores
            <>
              <Link to="/dashboard" className={isActive('/dashboard') ? 'active' : ''}>
                📊 Dashboard
              </Link>
              <Link to="/operadores" className={isActive('/operadores') ? 'active' : ''}>
                👤 Operadores
              </Link>
              <Link to="/vehiculos" className={isActive('/vehiculos') ? 'active' : ''}>
                🚛 Vehículos
              </Link>
              <Link to="/remolques" className={isActive('/remolques') ? 'active' : ''}>
                🚚 Remolques
              </Link>
              <Link to="/documentos" className={isActive('/documentos') ? 'active' : ''}>
                📄 Documentos
              </Link>
              <Link to="/viajes" className={isActive('/viajes') ? 'active' : ''}>
                🗺️ Viajes
              </Link>
              <Link to="/novedades" className={isActive('/novedades') ? 'active' : ''}>
                ⚠️ Novedades
              </Link>
              <Link to="/inspecciones" className={isActive('/inspecciones') ? 'active' : ''}>
                🔍 Inspecciones
              </Link>
              <Link to="/reportes" className={isActive('/reportes') ? 'active' : ''}>
                📈 Reportes
              </Link>
              <Link to="/postulaciones" className={isActive('/postulaciones') ? 'active' : ''}>
                📝 Postulaciones
              </Link>
              <Link to="/mapa" className={isActive('/mapa') ? 'active' : ''}>
                🗺️ Mapa de Operadores
              </Link>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <p><strong>{user?.nombre}</strong></p>
            <p className="user-role">{user?.rol}</p>
          </div>
          <button onClick={handleLogout} className="logout-button">
            Cerrar Sesión
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="top-header">
          <h1>{getPageTitle(location.pathname, user?.rol)}</h1>
        </header>
        <div className="content">
          {children}
        </div>
      </main>
    </div>
  );
};

const getPageTitle = (pathname: string, userRole?: string): string => {
  const titles: { [key: string]: string } = {
    '/dashboard': 'Dashboard',
    '/operadores': 'Operadores',
    '/vehiculos': 'Vehículos',
    '/remolques': 'Remolques',
    '/documentos': userRole === 'operador' ? 'Mis Documentos' : 'Documentos',
    '/viajes': 'Viajes',
    '/novedades': 'Novedades',
    '/inspecciones': 'Inspecciones',
    '/reportes': 'Reportes',
    '/postulaciones': 'Postulaciones',
    '/mapa': 'Mapa de Operadores'
  };
    return titles[pathname] || 'Enerpetrol';
  };

export default Layout;

